import { buildSearchQuery } from '@/utils/searchQuery';
import { DataLoaderTypedParams } from '@/types/DataLoaderParams';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/actions';
import { fetchAllCategoriesLandingPageData, fetchCategoriesIfNeeded } from '@/redux/modules/category';
import { fetchAuctionNews } from '@/redux/modules/auctionNews';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalogAd';
import { fetchCatalogIds, getCatalogIdsForCategory } from '@/redux/modules/categoryCatalogCards';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalog.actions';
import { fetchCategoryRelatedSearches } from '@/redux/modules/categoryRelatedSearches';
import {
    fetchCombinationCategory,
    fetchCombinationRelatedSearches,
    fetchCombinationSuggestedCategories,
} from '@/redux/modules/combinationCategory';
import { fetchItemCardModelsIfNeeded, getItemCardModel } from '@/redux/modules/itemCardModel';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import {
    fetchSearchFacetsIfNeeded,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
} from '@/redux/modules/searchFacets';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import { fetchSubcategories } from '@/redux/modules/subcategoryTiles';
import { fetchSuggestedCategories } from '@/redux/modules/suggestedCategoryTiles';
import {
    getCategoryIdsFromPathname,
    getCategoryNameFromPathname,
    getCombinationCategoryIdsFromPath,
    getFinalCategoryIdFromPathname,
} from '@/utils/urls';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { isArchivedSearch, parseQueryParams } from '@/utils/queryParams';
import { isUserLoggedIn } from '@/redux/modules/user';
import { searchCategory } from '@/redux/modules/landingPage';
import { submitIsSavedSearchCheck } from '@/redux/modules/saveSearchCheck';

const DEFAULT_PRICE_GUIDE_SORT = '-saleStart';
export const DATA_LOADING_ID = 'category-results-plus-promoted';

export default ({
    location,
    match,
    store,
}: DataLoaderTypedParams<{ categoryId: string; categoryId2: string; deleted?: number }>) => {
    const { params } = match;
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const pageName = pathname.includes('price-guide') ? 'price-guide' : 'category';
    const promises = [
        dispatch(fetchSearchFacetsIfNeeded(archivedSearch)),
        dispatch(fetchCatalogAdIfNeeded({ categoryIds: getCategoryIdsFromPathname(pathname), pageName })),
        dispatch(fetchAuctionNews(getCategoryNameFromPathname(pathname))),
        dispatch(fetchSubcategories(getFinalCategoryIdFromPathname(pathname))),
        dispatch(fetchCategoryRelatedSearches(getFinalCategoryIdFromPathname(pathname))),
        dispatch(fetchSuggestedCategories(getFinalCategoryIdFromPathname(pathname))),
        dispatch(fetchCatalogIds(getCategoryNameFromPathname(pathname))),
    ];
    let combinationIds;
    if (params.categoryId2) {
        // combinationIds encompass both the canonical url ids (categoryId1 and categoryId2) AND any ids from
        // the query params, eg category_id=123,234
        combinationIds = getCombinationCategoryIdsFromPath(params, queryParams);
        if (Boolean(combinationIds)) {
            promises.push(dispatch(fetchCombinationRelatedSearches(combinationIds)));
            promises.push(dispatch(fetchCombinationSuggestedCategories(combinationIds)));
        }
    }

    if (pathname === '/c/') {
        promises.push(dispatch(fetchAllCategoriesLandingPageData()));
    }

    return Promise.all(promises).then(() => {
        const currentState = getState();
        const defaultSortOrder = archivedSearch ? DEFAULT_PRICE_GUIDE_SORT : DEFAULT_SORT_ORDER;
        const pageSize = getSearchPageSize(currentState);
        const catalogIds = getCatalogIdsForCategory(currentState);

        let rawFacets;
        if (archivedSearch) {
            rawFacets = getRawArchivedSearchFacets(currentState);
        } else {
            rawFacets = getRawUpcomingSearchFacets(currentState);
        }
        const searchQuery = buildSearchQuery({
            defaultSortOrder,
            pathname,
            queryParams,
            rawFacets,
            searchPageSize: pageSize,
        });

        const userLoggedIn = isUserLoggedIn(currentState);
        let landingPageIds = []; // add more id's here

        if (params.categoryId2) {
            landingPageIds = [Number(params.categoryId), Number(params.categoryId2)]; // this is probably duplicated with 'getCombinationCategoryIdsFromPath'
        } else if (params.categoryId) {
            landingPageIds = [Number(params.categoryId)];
        }

        const promises = [
            dispatch(fetchCatalogsIfNeeded({ catalogIds })),
            dispatch(searchCategory(searchQuery, landingPageIds)),
            dispatch(fetchCategoriesIfNeeded(landingPageIds)),
        ];

        if (userLoggedIn) {
            const ssParams = getSaveSearchParams(queryParams, pathname);
            promises.push(dispatch(submitIsSavedSearchCheck(ssParams)));
        }

        return Promise.all(promises).then(() => {
            const searchState = getState();
            const itemIds = getSearchedItemIds(searchState);
            const promises = [];

            // combination category page data is needed AFTER category page data,
            // because the combination meta values need to override the standard category values
            if (combinationIds) {
                promises.push(dispatch(fetchCombinationCategory(combinationIds)));
            }

            // make an array of requests to issue, dispatching analytics after they all complete
            // i have no idea why we care about `params.deleted` but apparently if it's
            // there we need to also fetch the item with its id
            if (itemIds?.length) {
                if (params?.deleted && !isNaN(params.deleted)) {
                    promises.push(
                        dispatch(fetchItemCardModelsIfNeeded([params.deleted], 'MY-SAVED-SEARCH-PAGE-DELETED'))
                    );
                }
                return Promise.all(promises);
            } else {
                const { soldItemIds = [] } = getSearchUiState(searchState);
                promises.push(dispatch(fetchItemCardModelsIfNeeded(soldItemIds, 'MY-SAVED-SEARCH-PAGE-SOLD')));
                promises.push(dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])));

                return Promise.all(promises).then(() => {
                    const state = getState();
                    const { itemId, sellerId, title } = getItemCardModel(state, soldItemIds[0]);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);
                    return Promise.all([
                        dispatch(
                            fetchSimilarItemsIfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            })
                        ),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, itemId);
                        return dispatch(fetchItemCardModelsIfNeeded(similarItemIds, 'MY-SAVED-SEARCH-PAGE-SIMILAR'));
                    });
                });
            }
        });
    });
};
